import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, descargar } from "../lib/api";
import { creditos, fechaHora, formatearCantidad, gramos, iso } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, TarjetaDato, ThOrden, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import Ticket from "../componentes/Ticket";
import { useIdioma, type Variables } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * El historial de todo lo que ha salido por el mostrador.
 *
 * Va en dos pestañas y no en una lista sola a proposito. La competencia lo
 * junta todo en «Orders», pero aqui el dispensario es el libro que hay que
 * poder enseñar si alguien lo pide, y meter cervezas y cafes dentro de ese
 * libro lo ensucia. Los filtros y las columnas si son los mismos en las dos:
 * lo que se busca es siempre lo mismo —quien, cuando, quien lo atendio—.
 */

interface Dispensacion {
  id: string;
  numero: number;
  fecha: string;
  totalMg: number;
  totalCentimos: number;
  descuentoCentimos: number;
  descuentoManualCentimos: number;
  descuentoManualTexto: string | null;
  metodoPago: string;
  estado: string;
  motivoAnulacion: string | null;
  /** Hecha sin red y enviada al volver, con lo que se saltó apuntado. */
  sinConexion?: boolean;
  incidencias?: string | null;
  articulos: number;
  socio: { id: string; numeroSocio: number; nombre: string; apellidos: string };
  usuario: { id: string; nombre: string } | null;
  /** Quien la subio, cuando no es quien la firma. Lo de la cola sin red. */
  subidaPor: { id: string; nombre: string } | null;
  puesto: { id: string; nombre: string } | null;
  lineas: { nombreProducto: string; cantidadMg: number; subtotalCentimos: number; unidad: string }[];
}

interface VentaBarra {
  id: string;
  numero: number;
  fecha: string;
  totalCentimos: number;
  metodoPago: string;
  estado: string;
  motivoAnulacion: string | null;
  articulos: number;
  socio: { id: string; numeroSocio: number; nombre: string; apellidos: string } | null;
  usuario: { id: string; nombre: string } | null;
  puesto: { id: string; nombre: string } | null;
  lineas: { nombre: string; cantidad: number; totalCentimos: number }[];
}

/** Lo que pinta la tabla, venga de donde venga. */
interface Fila {
  id: string;
  numero: number;
  fecha: string;
  socio: { id: string; numeroSocio: number; nombre: string; apellidos: string } | null;
  productos: string;
  articulos: number;
  /** Solo el dispensario pesa; la barra vende unidades. */
  mg: number | null;
  descuentoCentimos: number;
  /** Lo que alguien rebajo a mano, aparte de la tarifa. */
  descuentoManualCentimos: number;
  descuentoManualTexto: string | null;
  totalCentimos: number;
  metodoPago: string;
  estado: string;
  sinConexion: boolean;
  incidencias: string | null;
  /** Quien lo atendio y, si el club usa puestos, desde cual. */
  atendio: string;
  /**
   * Quien la subio, solo cuando no es quien la firma.
   *
   * Pasa con lo que se hizo sin red: lo sube la primera sesion que se abra
   * despues. Se enseña porque desde que la tablet dice quien atendio, alguien
   * podria decir que fue otro, y eso tiene que verse aqui y no solo en la
   * auditoria.
   */
  subidaPor: string | null;
}

type Pestana = "dispensario" | "barra";
type Columna = "numero" | "fecha" | "socio" | "articulos" | "cantidad" | "descuento" | "importe";

export default function Dispensaciones() {
  const { t } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const hoy = iso(new Date());
  const [pestana, setPestana] = useState<Pestana>("dispensario");
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [buscar, setBuscar] = useState("");
  const [usuarioId, setUsuarioId] = useState("");
  const [puestoId, setPuestoId] = useState("");
  const [estado, setEstado] = useState("");
  const [pagina, setPagina] = useState(1);
  const [orden, setOrden] = useState<Columna>("fecha");
  const [descendente, setDescendente] = useState(true);
  const [anulando, setAnulando] = useState<Fila | null>(null);
  const [verTicket, setVerTicket] = useState<Fila | null>(null);
  const [error, setError] = useState("");

  const verBarra = puede("venderBarra");
  /** Cambiar un filtro y quedarse en la pagina 7 deja la pantalla vacia sin decir por que. */
  const filtrar = (poner: (v: string) => void) => (v: string) => {
    poner(v);
    setPagina(1);
  };
  const filtros = {
    desde,
    hasta,
    buscar: buscar.trim() || undefined,
    usuarioId: usuarioId || undefined,
    puestoId: puestoId || undefined,
    estado: estado || undefined,
  };

  const dispensaciones = useQuery({
    queryKey: ["dispensaciones", filtros, pagina],
    enabled: pestana === "dispensario",
    queryFn: () =>
      api<{
        total: number;
        dispensaciones: Dispensacion[];
        anulados: number;
        totales: { centimos: number; mg: number; descuentoCentimos: number; descuentoManualCentimos: number };
      }>("/dispensaciones", { query: { ...filtros, pagina, porPagina } }),
  });

  const ventas = useQuery({
    queryKey: ["barra-historial", filtros],
    enabled: pestana === "barra",
    queryFn: () => api<VentaBarra[]>("/barra/ventas", { query: filtros }),
  });

  const equipo = useQuery({
    queryKey: ["equipo"],
    queryFn: () => api<{ id: string; nombre: string; activo: boolean }[]>("/club/equipo"),
  });
  const puestos = useQuery({
    queryKey: ["puestos"],
    queryFn: () => api<{ id: string; nombre: string; activo: boolean }[]>("/club/puestos"),
  });

  const filas: Fila[] = useMemo(() => {
    const sinOrdenar: Fila[] =
      pestana === "dispensario"
        ? (dispensaciones.data?.dispensaciones ?? []).map((d) => ({
            id: d.id,
            numero: d.numero,
            fecha: d.fecha,
            socio: d.socio,
            productos: d.lineas
              .map((l) => `${l.nombreProducto} ${formatearCantidad(l.cantidadMg, l.unidad)}`)
              .join(" · "),
            articulos: d.articulos,
            mg: d.totalMg,
            descuentoCentimos: d.descuentoCentimos,
            descuentoManualCentimos: d.descuentoManualCentimos,
            descuentoManualTexto: d.descuentoManualTexto,
            totalCentimos: d.totalCentimos,
            metodoPago: d.metodoPago,
            estado: d.estado,
            sinConexion: d.sinConexion ?? false,
            incidencias: d.incidencias ?? null,
            atendio: quienAtendio(d),
            subidaPor: d.subidaPor?.nombre ?? null,
          }))
        : (ventas.data ?? []).map((v) => ({
            id: v.id,
            numero: v.numero,
            fecha: v.fecha,
            socio: v.socio,
            productos: v.lineas.map((l) => `${l.cantidad}× ${l.nombre}`).join(" · "),
            articulos: v.articulos,
            mg: null,
            descuentoCentimos: 0,
            descuentoManualCentimos: 0,
            descuentoManualTexto: null,
            totalCentimos: v.totalCentimos,
            metodoPago: v.metodoPago,
            estado: v.estado,
            sinConexion: false,
            incidencias: null,
            atendio: quienAtendio(v),
            // La barra no tiene cola sin conexion: se cobra en el momento.
            subidaPor: null,
          }));

    const signo = descendente ? -1 : 1;
    return [...sinOrdenar].sort((a, b) => {
      switch (orden) {
        case "numero":
          return signo * (a.numero - b.numero);
        case "socio":
          return signo * nombreDe(a).localeCompare(nombreDe(b), "es");
        case "articulos":
          return signo * (a.articulos - b.articulos);
        case "cantidad":
          return signo * ((a.mg ?? 0) - (b.mg ?? 0));
        case "descuento":
          return (
            signo *
            (a.descuentoCentimos + a.descuentoManualCentimos - (b.descuentoCentimos + b.descuentoManualCentimos))
          );
        case "importe":
          return signo * (a.totalCentimos - b.totalCentimos);
        default:
          return signo * (new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
      }
    });
  }, [pestana, dispensaciones.data, ventas.data, orden, descendente]);

  const ordenarPor = (columna: Columna) => {
    if (columna === orden) setDescendente((d) => !d);
    else {
      setOrden(columna);
      setDescendente(columna !== "socio");
    }
  };

  const cargando = pestana === "dispensario" ? dispensaciones.isLoading : ventas.isLoading;
  const paginas =
    pestana === "dispensario" && dispensaciones.data
      ? Math.max(1, Math.ceil(dispensaciones.data.total / porPagina))
      : 1;
  const hayFiltros = Boolean(buscar.trim() || usuarioId || puestoId || estado);

  /* Los totales del dispensario los suma la API sobre todas las paginas; los de
     la barra vienen de las filas, que llegan todas. En las dos cuenta solo lo
     que no esta anulado: un ticket anulado no es dinero. */
  const completadas = filas.filter((f) => f.estado === "COMPLETADA");
  const anulados =
    pestana === "dispensario" ? (dispensaciones.data?.anulados ?? 0) : filas.length - completadas.length;
  const totalCentimos =
    pestana === "dispensario"
      ? (dispensaciones.data?.totales.centimos ?? 0)
      : completadas.reduce((s, f) => s + f.totalCentimos, 0);
  const totalDescuento = pestana === "dispensario" ? (dispensaciones.data?.totales.descuentoCentimos ?? 0) : 0;
  const totalDescuentoManual =
    pestana === "dispensario" ? (dispensaciones.data?.totales.descuentoManualCentimos ?? 0) : 0;
  const cuantas = pestana === "dispensario" ? (dispensaciones.data?.total ?? 0) : filas.length;

  return (
    <>
      <Cabecera titulo={t("d.titulo")} descripcion={t("d.descripcion")}>
        <input type="date" value={desde} onChange={(e) => filtrar(setDesde)(e.target.value)} style={{ width: 165 }} />
        <span className="suave">{t("d.a")}</span>
        <input type="date" value={hasta} onChange={(e) => filtrar(setHasta)(e.target.value)} style={{ width: 165 }} />
        {puede("verInformes") && (
          <button
            className="boton secundario"
            onClick={() =>
              descargar(
                `/exportar/${pestana === "dispensario" ? "dispensaciones" : "barra"}.csv?desde=${desde}&hasta=${hasta}`,
                `${pestana === "dispensario" ? "dispensaciones" : "barra"}-${desde}-a-${hasta}.csv`,
              ).catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("d.falloExportar")))
            }
          >
            {t("d.exportar")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      {verBarra && (
        <div className="pestanas">
          {(
            [
              ["dispensario", t("d.dispensario")],
              ["barra", t("d.barra")],
            ] as [Pestana, string][]
          ).map(([clave, texto]) => (
            <button
              key={clave}
              className={`pestana${pestana === clave ? " activa" : ""}`}
              onClick={() => {
                setPestana(clave);
                setPagina(1);
                setOrden("fecha");
                setDescendente(true);
              }}
            >
              {texto}
            </button>
          ))}
        </div>
      )}

      <Panel>
        <div className="fila ajustable" style={{ gap: 8 }}>
          <input
            placeholder={t("d.buscar")}
            value={buscar}
            onChange={(e) => filtrar(setBuscar)(e.target.value)}
            style={{ flex: "1 1 240px", minWidth: 200 }}
          />
          <select value={usuarioId} onChange={(e) => filtrar(setUsuarioId)(e.target.value)} style={{ maxWidth: 190 }}>
            <option value="">{t("d.atendioCualquiera")}</option>
            {(equipo.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
                {u.activo ? "" : t("d.baja")}
              </option>
            ))}
          </select>
          {(puestos.data ?? []).length > 0 && (
            <select value={puestoId} onChange={(e) => filtrar(setPuestoId)(e.target.value)} style={{ maxWidth: 170 }}>
              <option value="">{t("d.todosLosPuestos")}</option>
              {(puestos.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          )}
          <select value={estado} onChange={(e) => filtrar(setEstado)(e.target.value)} style={{ maxWidth: 160 }}>
            <option value="">{t("d.todos")}</option>
            <option value="COMPLETADA">{t("d.soloValidos")}</option>
            <option value="ANULADA">{t("d.soloAnulados")}</option>
          </select>
          {hayFiltros && (
            <button
              className="boton fantasma"
              onClick={() => {
                setBuscar("");
                setUsuarioId("");
                setPuestoId("");
                setEstado("");
                setPagina(1);
              }}
            >
              {t("d.quitarFiltros")}
            </button>
          )}
        </div>
      </Panel>

      <div className={`rejilla ${pestana === "dispensario" ? "tres" : "dos"}`} style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={pestana === "dispensario" ? t("d.entregas") : t("d.cobros")}
          valor={String(cuantas)}
          pie={anulados === 0 ? undefined : anulados === 1 ? t("d.unAnulado") : t("d.anulados", { n: anulados })}
        />
        {pestana === "dispensario" && (
          <TarjetaDato etiqueta={t("d.cantidad")} valor={gramos(dispensaciones.data?.totales.mg ?? 0)} />
        )}
        <TarjetaDato
          etiqueta={t("d.aportaciones")}
          valor={creditos(totalCentimos)}
          pie={
            [
              totalDescuento > 0 ? t("d.deDescuento", { importe: creditos(totalDescuento) }) : "",
              totalDescuentoManual > 0 ? t("d.aMano", { importe: creditos(totalDescuentoManual) }) : "",
            ]
              .filter(Boolean)
              .join(" · ") || undefined
          }
        />
      </div>

      <Panel sinCuerpo>
        {cargando ? (
          <Cargando />
        ) : filas.length === 0 ? (
          <Vacio>
            {hayFiltros
              ? t("d.sinCoincidencias")
              : pestana === "dispensario"
                ? t("d.sinDispensaciones")
                : t("d.sinCobros")}
          </Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <ThOrden columna="numero" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                    {t("d.numero")}
                  </ThOrden>
                  <ThOrden columna="fecha" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                    {t("d.fecha")}
                  </ThOrden>
                  <ThOrden columna="socio" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                    {t("d.socio")}
                  </ThOrden>
                  <th>{t("d.productos")}</th>
                  <ThOrden columna="articulos" orden={orden} descendente={descendente} alOrdenar={ordenarPor} num>
                    {t("d.articulos")}
                  </ThOrden>
                  {pestana === "dispensario" && (
                    <>
                      <ThOrden columna="cantidad" orden={orden} descendente={descendente} alOrdenar={ordenarPor} num>
                        {t("d.cantidad")}
                      </ThOrden>
                      <ThOrden columna="descuento" orden={orden} descendente={descendente} alOrdenar={ordenarPor} num>
                        {t("d.descuento")}
                      </ThOrden>
                    </>
                  )}
                  <ThOrden columna="importe" orden={orden} descendente={descendente} alOrdenar={ordenarPor} num>
                    {t("d.importe")}
                  </ThOrden>
                  <th>{t("d.atendio")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.id} style={f.estado === "ANULADA" ? { opacity: 0.55 } : undefined}>
                    <td className="tabular suave">{f.numero}</td>
                    <td className="pequeno">{fechaHora(f.fecha)}</td>
                    <td>
                      {f.socio ? (
                        <Link to={`/socios/${f.socio.id}`}>
                          #{f.socio.numeroSocio} {f.socio.nombre} {f.socio.apellidos}
                        </Link>
                      ) : (
                        <span className="suave">{t("d.sinSocio")}</span>
                      )}
                      {f.estado === "ANULADA" && (
                        <span className="etiqueta roja" style={{ marginLeft: 6 }}>
                          {t("d.anulada")}
                        </span>
                      )}
                      {f.sinConexion && (
                        <span
                          className="etiqueta azul"
                          style={{ marginLeft: 6 }}
                          title={t("d.sinConexionAyuda")}
                        >
                          {t("d.sinConexion")}
                        </span>
                      )}
                      {f.incidencias && (
                        // Lo que el mostrador habria parado y sin red solo se apunto: es lo que hay que mirar.
                        <div className="ambar pequeno" style={{ marginTop: 4 }}>
                          {f.incidencias}
                        </div>
                      )}
                    </td>
                    <td className="pequeno suave">{f.productos}</td>
                    <td className="num tabular">{f.articulos}</td>
                    {pestana === "dispensario" && (
                      <>
                        <td className="num tabular">{gramos(f.mg ?? 0)}</td>
                        <td className="num tabular">
                          {f.descuentoCentimos > 0 ? creditos(f.descuentoCentimos) : <span className="suave">—</span>}
                          {/*
                            La rebaja de mano se enseña aparte de la de tarifa y
                            en verde: es la unica que ha decidido una persona, y
                            es la que se busca cuando un turno no cuadra.
                          */}
                          {f.descuentoManualCentimos > 0 && (
                            <div className="pequeno" style={{ color: "var(--verde)" }}>
                              {t("d.descuentoAMano", { importe: creditos(f.descuentoManualCentimos) })}
                              {f.descuentoManualTexto ? ` (${f.descuentoManualTexto})` : ""}
                            </div>
                          )}
                        </td>
                      </>
                    )}
                    <td className="num tabular">
                      {creditos(f.totalCentimos)}
                      <div className="suave pequeno">{t(`pago.${f.metodoPago}` as Clave)}</div>
                    </td>
                    <td className="pequeno suave">
                      {f.atendio}
                      {f.subidaPor && (
                        <div>
                          <Etiqueta tipo="ambar">
                            {t("d.laSubio", { nombre: f.subidaPor })}
                          </Etiqueta>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="fila" style={{ gap: 2, justifyContent: "flex-end" }}>
                        {pestana === "dispensario" && (
                          <button className="boton fantasma" onClick={() => setVerTicket(f)}>
                            {t("d.ticket")}
                          </button>
                        )}
                        {puede("anularDispensacion") && f.estado === "COMPLETADA" && (
                          <button className="boton fantasma" onClick={() => setAnulando(f)}>
                            {t("d.anular")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {paginas > 1 && (
          <div className="panel-cabecera" style={{ borderTop: "1px solid var(--borde)", borderBottom: 0 }}>
            <span className="suave pequeno">
              {t("g.pagina", { pagina, paginas })}
            </span>
            <div className="fila">
              <button className="boton secundario" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                {t("g.anterior")}
              </button>
              <button className="boton secundario" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
                {t("g.siguiente")}
              </button>
            </div>
          </div>
        )}

        {pestana === "barra" && filas.length >= TOPE_BARRA && (
          <div className="panel-cabecera" style={{ borderTop: "1px solid var(--borde)", borderBottom: 0 }}>
            <span className="suave pequeno">
              {t("d.tope", { tope: TOPE_BARRA })}
            </span>
          </div>
        )}
      </Panel>

      {verTicket && (
        <Modal
          titulo={t("d.ticketNumero", { numero: verTicket.numero })}
          alCerrar={() => setVerTicket(null)}
          acciones={
            <>
              <button className="boton secundario" onClick={() => setVerTicket(null)}>
                {t("g.cerrar")}
              </button>
              <button className="boton" onClick={() => window.print()}>
                {t("d.imprimir")}
              </button>
            </>
          }
        >
          <div className="ticket-previsualizacion">
            <Ticket dispensacionId={verTicket.id} />
          </div>
        </Modal>
      )}

      {anulando && (
        <DialogoAnular
          fila={anulando}
          pestana={pestana}
          alCerrar={() => setAnulando(null)}
          alFallar={setError}
          alHecho={() => {
            setAnulando(null);
            void clienteConsultas.invalidateQueries({ queryKey: ["dispensaciones"] });
            void clienteConsultas.invalidateQueries({ queryKey: ["barra-historial"] });
            void clienteConsultas.invalidateQueries({ queryKey: ["barra-ventas"] });
          }}
        />
      )}
    </>
  );
}

const porPagina = 50;
/** Lo que como mucho devuelve el historial de la barra de una vez. */
const TOPE_BARRA = 500;

const nombreDe = (f: Fila) => (f.socio ? `${f.socio.apellidos} ${f.socio.nombre}` : "");

function quienAtendio(t: { usuario: { nombre: string } | null; puesto: { nombre: string } | null }) {
  if (!t.usuario) return "—";
  return t.puesto ? `${t.usuario.nombre} · ${t.puesto.nombre}` : t.usuario.nombre;
}

/**
 * Lo que va a pasar al anular, dicho en una frase.
 *
 * Son seis frases enteras y no tres cachos pegados porque el orden de las
 * partes no es el mismo en todos los idiomas.
 */
function queSeDeshace(
  t: (clave: Clave, variables?: Variables) => string,
  fila: Fila,
  esDispensacion: boolean,
) {
  const cantidad = gramos(fila.mg ?? 0);
  const importe = creditos(fila.totalCentimos);
  if (fila.metodoPago === "CARTERA") {
    return esDispensacion
      ? t("d.an.dispensarioCartera", { cantidad, importe })
      : t("d.an.barraCartera", { importe });
  }
  if (fila.metodoPago === "EFECTIVO") {
    return esDispensacion ? t("d.an.dispensarioCaja", { cantidad, importe }) : t("d.an.barraCaja", { importe });
  }
  return esDispensacion ? t("d.an.dispensario", { cantidad }) : t("d.an.barra");
}

function DialogoAnular({
  fila,
  pestana,
  alCerrar,
  alHecho,
  alFallar,
}: {
  fila: Fila;
  pestana: Pestana;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const esDispensacion = pestana === "dispensario";

  return (
    <Modal
      titulo={
        esDispensacion
          ? t("d.an.tituloDispensacion", { numero: fila.numero })
          : t("d.an.tituloVenta", { numero: fila.numero })
      }
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton peligro"
            disabled={enviando || motivo.trim().length < 3}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/${esDispensacion ? "dispensaciones" : "barra/ventas"}/${fila.id}/anular`, {
                  metodo: "POST",
                  cuerpo: { motivo },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("d.an.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("d.an.anular")}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>{queSeDeshace(t, fila, esDispensacion)}</p>
      <Campo etiqueta={t("d.an.motivo")}>
        <textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
      </Campo>
    </Modal>
  );
}
